import {
  Box,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { ReactNode, useState } from 'react';

import { CollapsibleSection } from 'components/atoms/CollapsibleSection';
import { detectBrowserOperatingSystem } from 'utils/osDetection';
import { StepHelp, StepHelpItem } from './StepHelp';
import { AWS_CLI_ACCELERATED_ENDPOINT_OPTION } from './cliInstructionItNotes';
import { OperatingSystem } from './cliInstructionTypes';
import { getTerminalOpenGuidance } from './useCliInstructionSteps';

type SourceMode = 'recursive' | 'single_file';

type SourceModeLabels = {
  recursive: string;
  singleFile: string;
};

const uploadHelpItems = (sourceType: 'local' | 's3'): StepHelpItem[] => [
  sourceType === 's3'
    ? {
        question: 'Where do I get the path?',
        answer: 'In the Amazon S3 console, open the folder and copy its S3 URI. It starts with s3://.',
      }
    : {
        question: 'Where do I get the path?',
        answer: 'Use the path instructions under the field to copy a folder or file path.',
      },
  {
    question: 'Will every file upload?',
    answer:
      'Folder mode uploads every file in that folder, including files Nucleai will not open. Use a folder that contains only the slides.',
  },
  sourceType === 's3'
    ? {
        question: 'Can I remove parts of the command?',
        answer: 'Keep --acl and --sse. They let Nucleai read the files and store them encrypted.',
      }
    : {
        question: 'Can I remove parts of the command?',
        answer: "Keep the endpoint option. It sends the upload through Amazon's faster transfer address.",
      },
  ...(sourceType === 's3'
    ? [
        {
          question: 'The command says AccessDenied.',
          answer: 'Your AWS administrator still needs to allow this role to read the source bucket.',
        },
      ]
    : []),
];

type AwsUploadCommandGeneratorProps = {
  copyableCommandBlock: (command: string) => ReactNode;
  destination: string;
  operatingSystem?: OperatingSystem;
  sourceType: 'local' | 's3';
  invalidSourceText?: string;
  isSourceValid?: (source: string) => boolean;
  sourcePathInstructions?: ReactNode;
  sourceLabels: SourceModeLabels;
  sourcePlaceholders?: SourceModeLabels;
  uploadOptions?: string[];
};

const buildAwsUploadCommand = ({
  destination,
  recursive,
  source,
  uploadOptions = [],
}: {
  destination: string;
  recursive: boolean;
  source: string;
  uploadOptions?: string[];
}) => {
  const trimmedSource = source.trim();

  const normalizedSource =
    trimmedSource.length >= 2 && trimmedSource.startsWith('"') && trimmedSource.endsWith('"')
      ? trimmedSource.slice(1, -1).trim()
      : trimmedSource;

  if (!normalizedSource) {
    return '';
  }

  const isS3Source = normalizedSource.toLowerCase().startsWith('s3://');

  return [
    'aws s3 cp',
    `"${normalizedSource}"`,
    destination,
    ...uploadOptions,
    // AWS CLI applies --endpoint-url to source listing as well as destination writes.
    // For S3-to-S3 copies, skip acceleration unless the source bucket is known to support it.
    isS3Source ? undefined : AWS_CLI_ACCELERATED_ENDPOINT_OPTION,
    recursive ? '--recursive' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
};

const AwsUploadCommandGenerator = ({
  copyableCommandBlock,
  destination,
  operatingSystem,
  sourceType,
  invalidSourceText,
  isSourceValid,
  sourcePathInstructions,
  sourceLabels,
  sourcePlaceholders,
  uploadOptions,
}: AwsUploadCommandGeneratorProps) => {
  const [source, setSource] = useState('');
  const [mode, setMode] = useState<SourceMode>('recursive');
  const recursive = mode === 'recursive';
  const trimmedSource = source.trim();
  const sourceIsValid = trimmedSource ? (isSourceValid ? isSourceValid(trimmedSource) : true) : true;
  const command =
    trimmedSource && sourceIsValid ? buildAwsUploadCommand({ destination, recursive, source, uploadOptions }) : '';
  const terminalOperatingSystem = operatingSystem || detectBrowserOperatingSystem();
  const sourceInstruction =
    sourceType === 's3'
      ? recursive
        ? 'Paste the S3 folder path. Every file under that path will be uploaded.'
        : 'Paste the S3 path of the file to upload.'
      : recursive
      ? 'Paste the full folder path. Every file in that folder and its subfolders will be uploaded.'
      : 'Paste the full path of the file to upload.';

  return (
    <Stack spacing={2}>
      <StepHelp items={uploadHelpItems(sourceType)} />
      <Stack spacing={2}>
        <FormControl size="small">
          <FormLabel
            id="aws-upload-source-mode-label"
            sx={{ color: 'text.primary', fontSize: 13, fontWeight: 700, mb: 1 }}
          >
            Upload
          </FormLabel>
          <ToggleButtonGroup
            aria-labelledby="aws-upload-source-mode-label"
            role="radiogroup"
            exclusive
            size="small"
            value={mode}
            onChange={(_event, newValue: SourceMode | null) => {
              if (!newValue) {
                return;
              }

              setMode(newValue);
            }}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'flex-start' },
              '& .MuiToggleButton-root': {
                px: 2,
                textTransform: 'none',
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="recursive" role="radio" aria-checked={recursive}>
              Folder
            </ToggleButton>
            <ToggleButton value="single_file" role="radio" aria-checked={!recursive}>
              File
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
        <TextField
          label={recursive ? sourceLabels.recursive : sourceLabels.singleFile}
          value={source}
          onChange={(event) => setSource(event.target.value)}
          placeholder={recursive ? sourcePlaceholders?.recursive : sourcePlaceholders?.singleFile}
          error={Boolean(trimmedSource) && !sourceIsValid}
          helperText={Boolean(trimmedSource) && !sourceIsValid ? invalidSourceText : sourceInstruction}
          fullWidth
        />
      </Stack>
      {sourcePathInstructions}
      {command ? (
        <>
          <Typography variant="body2" color="text.secondary">
            {getTerminalOpenGuidance(terminalOperatingSystem)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Run this in your terminal. Keep it open until the command finishes.
          </Typography>
          {copyableCommandBlock(command)}
        </>
      ) : null}
      <CollapsibleSection title="3DHISTECH MIRAX (.mrxs) requirements">
        <Stack spacing={1.5}>
          <Typography variant="body2">
            Upload the .mrxs file and the folder with the same name together. Choose Folder and enter their parent
            folder. Keep both names unchanged.
          </Typography>
          <Typography variant="body2">For example, a liver biopsy stained with H&E:</Typography>
          <Box component="pre" sx={{ m: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>
            {
              'slides/\n  Liver_biopsy_HE.mrxs\n  Liver_biopsy_HE/\n    Slidedat.ini\n    Index.dat\n    Data0000.dat\n    Data0001.dat'
            }
          </Box>
          <Typography variant="body2" color="text.secondary">
            Include every index and data file named in Slidedat.ini. The index is Index.dat when Slidedat.ini does not
            name another index. Files inside that folder belong to this slide. They are not separate slides. An
            incomplete package cannot be ingested.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Do not upload annotations, XML, lock files, or other files that are not part of the slide.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Other supported slides are usually one file: .svs, .ndpi, .tif, .tiff, .qptiff, .isyntax, .bif, or .czi.
            Olympus .vsi is also supported, with its matching data folder.
          </Typography>
        </Stack>
      </CollapsibleSection>
    </Stack>
  );
};

export default AwsUploadCommandGenerator;
